import angular, { IPromise, IDeferred } from 'angular'
import { ApiTax } from '@vend/types'

import { productTaxesStore } from '../factories/database/product-taxes-store'
import { CustomerTaxesStore } from '../factories/database/customer-taxes-store'
import { TaxesStore } from '../factories/database/taxes-store'
import { $log, $q } from '../ng-compat/imports'
import { RegisterManager } from './register-manager'
import { SyncManager } from './sync-manager'
import { RetailerSettingsManager } from './retailer-settings-manager'

const NO_TAX_GROUP_ID_KEY = 'no_tax_group_id'

export interface TaxInfo {
  tax: ApiTax | undefined
  taxType: 'default' | 'product' | 'customer'
}
/**
 * Provides methods for determining the tax to use for products etc.
 */
export class TaxManager {
  private activeOutletDeferred?: IDeferred<void> | null
  private defaultTax?: ApiTax
  private noTaxGroup?: ApiTax
  private whenInitialised?: IPromise<unknown>

  constructor(
    private readonly taxesStore: TaxesStore,
    private readonly customerTaxesStore: CustomerTaxesStore,
    private readonly retailerSettingsManager: RetailerSettingsManager,
    private readonly registerManager: RegisterManager,
    private readonly syncManager: SyncManager
  ) {
    'ngInject'
  }

  /**
   * Initialise the tax manager ensuring it is ready for use (once the default tax has been resolved).
   *
   * @return A promise resolved with the default tax once the tax manager is good to go
   */
  public init(): IPromise<ApiTax | undefined> {
    if (!this.whenInitialised) {
      this.whenInitialised = this.retailerSettingsManager
        .init()
        .then(() => this._init())
    }

    return this.whenInitialised.then(() => {
      if (this.defaultTax !== undefined) {
        return this.defaultTax
      } else {
        return this.determineDefaultTax()
      }
    })
  }

  /**
   * Get the correct tax for the given product; either the specific outlet product tax or the default tax
   * of the current active outlet.
   *
   * @param productId
   *      The product ID to determine the tax for
   * @param customerId (optional)
   *      The ID of the customer on the sale to determine the tax for, only passed when add/remove customer
   *
   * @return A promise resolved with the correct tax for the given product
   */
  public getTaxForProduct(
    productId: string,
    customerId?: string
  ): IPromise<TaxInfo> {
    const defaultTax: ApiTax | undefined = this.getDefaultTax()
    const defaultTaxInfo: TaxInfo = {
      tax: defaultTax,
      taxType: 'default',
    }

    return productTaxesStore.get(productId).then(outletProductTax => {
      if (outletProductTax) {
        return this.taxesStore.get(outletProductTax.tax_id).then(tax => {
          if (!tax) {
            $log.debug(
              `[Tax Manager] Outlet product tax missing from taxes store ${outletProductTax.tax_id}`
            )
            return defaultTaxInfo
          }
          return {
            tax,
            taxType: 'product',
          }
        })
      } else if (customerId) {
        // Check if the customer has tax info
        return this.customerTaxesStore.get(customerId).then(data => {
          if (!data?.tax_id) {
            return defaultTaxInfo
          }

          const customerTaxId = data.tax_id
          return this.taxesStore.get(customerTaxId).then(tax => {
            return tax ? { tax, taxType: 'customer' } : defaultTaxInfo
          })
        })
      } else {
        return defaultTaxInfo
      }
    })
  }

  /**
   * Get the default tax for the current active outlet.
   *
   * @return The default tax data for the current active outlet
   */
  public getDefaultTax(): ApiTax | undefined {
    if (!this.defaultTax) {
      $log.debug('[Tax Manager] Default tax not set')
    }

    return this.defaultTax
  }

  /**
   * Get the no tax group for the retailer.
   *
   * @return The no tax group for the retailer
   */
  public getNoTaxGroup(): Promise<ApiTax | undefined> {
    if (this.noTaxGroup !== undefined) {
      return Promise.resolve(this.noTaxGroup)
    }

    return this.taxesStore
      .get(this.retailerSettingsManager.get()[NO_TAX_GROUP_ID_KEY])
      .then(taxGroup => {
        this.noTaxGroup = taxGroup
        return taxGroup
      })
  }

  /**
   * Initialise the tax manager internals, binding to events etc.
   */
  private _init(): void {
    this.registerManager.on('activeOutletChange', (event: any) => {
      if (this.activeOutletDeferred) {
        // Clear _activeOutletDeferred so if we ever re-encounter no outlet in determineDefaultTax we reinstate it
        const deferred = this.activeOutletDeferred
        this.activeOutletDeferred = null

        // Resolve the deferred so all instances of determineDefaultTax waiting for an active outlet can resolve
        deferred.resolve()
      } else {
        this.determineDefaultTax()
      }
    })
  }

  /**
   * Determine the default tax for the current outlet and update the `defaultTax` variable (for `#getDefaultTax()`).
   *
   * @return A promise resolved with the default tax for the current active outlet
   */
  private determineDefaultTax(): IPromise<ApiTax | undefined> {
    const outlet = this.registerManager.getActiveOutlet()

    if (!outlet) {
      $log.log(
        `[Tax Manager] Missing active outlet when determining default tax`
      )

      if (!this.activeOutletDeferred) {
        this.activeOutletDeferred = $q.defer()
      }

      return this.activeOutletDeferred.promise.then(() =>
        this.determineDefaultTax()
      )
    }

    // Wait for taxes to finish syncing (in case a sync is in progress or the initial sync hasn't complete yet)
    return this.syncManager
      .whenCompleteEntityDataAvailable('taxes')
      .then(() => this.taxesStore.get(outlet.default_tax_id))
      .then((tax: ApiTax | undefined) => {
        if (!tax) {
          $log.debug(
            `[Tax Manager] Default tax missing from taxes store ${outlet.default_tax_id}`
          )
        } else {
          $log.debug(`[Tax Manager] Default tax set ${tax.id}`)
        }

        this.defaultTax = tax
        return tax
      })
  }
}

/**
 * Provides methods for determining the tax to use for products etc.
 *
 * @module taxManager
 */
angular.module('webregister.managers').factory('taxManager', TaxManager)
